import React, { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, CheckCircle2, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await axios.post(`${API}/contacts`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone || null,
        subject: form.subject || null,
        message: form.message.trim(),
      });
      setSuccess(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not send your message. Please call us.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="relative overflow-hidden grain">
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-[#1a0a2e] via-[#240a3d] to-[#2e0e54]" />
        <div className="max-w-5xl mx-auto px-5 lg:px-10 pt-20 pb-10 text-center">
          <div className="text-[#f0d479] text-xs tracking-[0.32em] uppercase">Contact</div>
          <h1 className="font-serif text-5xl md:text-6xl mt-3 text-[#f7f2e7]">
            Let's <span className="gold-text">talk</span>.
          </h1>
          <p className="text-[#dcd0b3] mt-5 max-w-2xl mx-auto">
            Questions about our homes, application process, or what to expect?
            We'd love to hear from you.
          </p>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-6xl mx-auto px-5 lg:px-10 grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2 space-y-5">
            <InfoCard
              icon={Phone}
              title="Call"
              value="706-431-8996"
              href="tel:7064318996"
              testId="contact-info-phone"
            />
            <InfoCard
              icon={Mail}
              title="Email"
              value="bedsofhaven@yahoo.com"
              href="mailto:bedsofhaven@yahoo.com"
              testId="contact-info-email"
            />
            <InfoCard
              icon={MapPin}
              title="Website"
              value="www.bedsofhaven.com"
              testId="contact-info-web"
            />
            <div className="card-purple p-7">
              <div className="text-[#f0d479] text-xs tracking-[0.32em] uppercase">Hours</div>
              <div className="mt-3 text-[#dcd0b3] space-y-1.5">
                <div className="flex justify-between"><span>Mon – Fri</span><span>9:00 – 6:00</span></div>
                <div className="flex justify-between"><span>Saturday</span><span>10:00 – 4:00</span></div>
                <div className="flex justify-between"><span>Sunday</span><span>By appointment</span></div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-3">
            {success ? (
              <div data-testid="contact-success" className="card-purple p-10 text-center">
                <CheckCircle2 className="mx-auto text-[#d4af37]" size={52} />
                <h2 className="font-serif text-3xl text-[#f7f2e7] mt-5">Message sent</h2>
                <p className="text-[#dcd0b3] mt-3">
                  Thank you — we'll get back to you shortly.
                </p>
                <button
                  data-testid="contact-reset-btn"
                  onClick={() => {
                    setSuccess(false);
                    setForm({ name: "", email: "", phone: "", subject: "", message: "" });
                  }}
                  className="btn-outline-gold mt-7"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form
                onSubmit={submit}
                data-testid="contact-form"
                className="card-purple p-7 md:p-10 space-y-5"
              >
                <div className="grid md:grid-cols-2 gap-5">
                  <Field label="Name *">
                    <input
                      required
                      data-testid="contact-input-name"
                      className="input-base"
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Your name"
                    />
                  </Field>
                  <Field label="Phone">
                    <input
                      data-testid="contact-input-phone"
                      className="input-base"
                      value={form.phone}
                      onChange={update("phone")}
                      placeholder="(optional)"
                    />
                  </Field>
                </div>
                <Field label="Email *">
                  <input
                    type="email"
                    required
                    data-testid="contact-input-email"
                    className="input-base"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@example.com"
                  />
                </Field>
                <Field label="Subject">
                  <input
                    data-testid="contact-input-subject"
                    className="input-base"
                    value={form.subject}
                    onChange={update("subject")}
                    placeholder="How can we help?"
                  />
                </Field>
                <Field label="Message *">
                  <textarea
                    required
                    rows={5}
                    data-testid="contact-input-message"
                    className="input-base resize-none"
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Share your question or anything you'd like us to know."
                  />
                </Field>

                {error && (
                  <div data-testid="contact-error" className="text-red-300 text-sm bg-red-900/30 border border-red-500/40 rounded-lg p-3">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-btn"
                  className="btn-gold w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? "Sending…" : (
                    <>Send Message <ArrowRight size={18} /></>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-sm tracking-wide text-[#f0d479] mb-2 inline-block">{label}</span>
      {children}
    </label>
  );
}

function InfoCard({ icon: Icon, title, value, href, testId }) {
  const inner = (
    <>
      <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#d4af37] to-[#b8902c] flex items-center justify-center text-[#1a0a2e]">
        <Icon size={20} />
      </div>
      <div className="mt-4">
        <div className="text-[#f0d479] text-xs tracking-[0.3em] uppercase">{title}</div>
        <div className="text-[#f7f2e7] mt-1 break-all">{value}</div>
      </div>
    </>
  );
  return href ? (
    <a href={href} data-testid={testId} className="card-purple p-7 block hover:translate-y-[-2px] transition-transform">
      {inner}
    </a>
  ) : (
    <div data-testid={testId} className="card-purple p-7">{inner}</div>
  );
}
